import { isObject, useLocalStorage, useSessionStorage } from '@vueuse/core'
import { jwtDecode } from 'jwt-decode'
import { defineStore } from 'pinia'
import { computed } from 'vue'

export type TokenList = { token: string }[]

/**
 * Token types issued by ada-api (`TokenService.TokenTypes`). The customer
 * portal only ever receives `CustomerUserToken`; the other variants
 * (`UserToken`, `ImageToken`, …) exist in the backend but are never held here.
 * The enum mirrors ada-app-ui so a token-type check reads the same across the
 * ecosystem.
 */
export enum TokenType {
  CustomerUserToken = 'CustomerUserToken',
}

/**
 * Decoded claims of a customer JWT, mirroring the payload built by
 * `TokenService.create_customer_token` in ada-api. Required fields are always
 * present on the token; optional fields only appear on specific login paths
 * (company login, proxy/fullmakt, staff signed-in-as). Legacy code reads a
 * subset of these via the Vuex bridge (`token_json`); the index signature keeps
 * that untyped access working.
 *
 * Note: the token carries no `u_email` or `refresh_count` — email comes from
 * the user resource and `refresh_count` lives in `user--authentications`.
 */
export interface AdaJwtCustomerUserTokenClaims {
  token_type: TokenType.CustomerUserToken
  token_id: string
  exp: number

  /** Customer user id */
  u_id: string
  /** Customer user display name */
  u_name: string
  /** Swedish mobile BankID personal number, or '' when signed in another way */
  id_nbr: string
  /** Debtor ids the token is scoped to */
  d_id: string[]
  /** Debtor display names, parallel to `d_id` */
  d_name: string[]

  ip: string
  features: string
  rlc: string
  rls: string
  origin_app: string | null

  /** Organisation number — present on company login */
  org_nbr?: string
  /** Authentication method used for this login */
  auth_method?: string
  d_unit_id?: string
  /** `true` on the very first login */
  first_login?: true
  /** Proxy (fullmakt) id when acting on another debtor's behalf */
  proxy?: string
  /** Set when an internal user is signed in as this customer */
  signed_in_by_u_id?: string
  /** Read-only session (signed in by another user; prod only) */
  readonly?: boolean
  /** Canonical id number Nova keys on: personal number, org number, or the represented debtor's */
  subject_id_nbr?: string
  /** `true` when the debtor has a protected identity (skyddad identitet) */
  protected_identity?: boolean
  /** BankID personal number of the person who authenticated, also on company and proxy login */
  authenticated_id_nbr?: string
  /** Company login only: `true` when the authenticated person may bind the company */
  company_signatory?: boolean

  [prop: string]: unknown
}

/** The only token shape the customer portal holds. */
export type AdaJwtClaims = AdaJwtCustomerUserTokenClaims

export function isCustomerUserToken(payload: unknown): payload is AdaJwtCustomerUserTokenClaims {
  return isObject(payload) && 'token_type' in payload && payload.token_type === TokenType.CustomerUserToken
}

// useLocalStorage/useSessionStorage for a key should only be called once per tab since
// listenForChanges which works across tabs doesn't work in the same tab as the
// change was made.
const persistentTokens = useLocalStorage('tokens', {
  tokens: [] as { token: string }[],
  selectedToken: 0,
})

const sessionTokens = useSessionStorage('tokens', {
  tokens: [] as { token: string }[],
  selectedToken: 0,
  sessionEnd: undefined as number | undefined,
})

export interface SetTokenListOptions {
  /** Uses the current storage if undefined or persistent if no tokens are currently stored */
  storage?: 'session' | 'persistent'
  sessionTimeoutMinutes?: number
}

const useAuthStore = defineStore('auth', () => {
  const authPersistence = computed<'session' | 'persistent'>(() =>
    sessionTokens.value.tokens.length > 0 ? 'session' : 'persistent'
  )

  const activeTokenStorage = computed(() =>
    authPersistence.value === 'session' ? sessionTokens.value : persistentTokens.value
  )

  const sessionEnd = computed(() => sessionTokens.value.sessionEnd)

  const isSessionExpired = computed(() => {
    if (sessionTokens.value.sessionEnd == null) return false
    return Date.now() > sessionTokens.value.sessionEnd
  })

  const tokensList = computed(() => {
    if (isSessionExpired.value) {
      return []
    }

    return activeTokenStorage.value.tokens.map(({ token }, idx) => {
      let payload = null
      try {
        payload = jwtDecode<AdaJwtClaims>(token)
        // eslint-disable-next-line no-empty
      } catch {}
      return {
        token,
        payload,
        selected: idx === activeTokenStorage.value.selectedToken,
      }
    })
  })
  const selectedToken = computed(() => tokensList.value.find((t) => t.selected))

  /**
   * A company session whose signed-in person may not bind the company: refunds are read-only.
   * An absent claim counts as not authorized, the same fail-closed rule the servers apply.
   */
  const isCompanyWithoutSignatory = computed(() => {
    const claims = selectedToken.value?.payload
    return claims?.org_nbr != null && claims.company_signatory !== true
  })

  function setTokenList(tokenList: TokenList, options?: SetTokenListOptions) {
    const _currentToken = selectedToken.value?.token
    const targetStorage = options?.storage ?? authPersistence.value

    if (targetStorage === 'session') {
      persistentTokens.value.tokens = []
      persistentTokens.value.selectedToken = 0

      sessionTokens.value.tokens = tokenList
      if (options?.sessionTimeoutMinutes != null) {
        sessionTokens.value.sessionEnd = Date.now() + options.sessionTimeoutMinutes * 60 * 1000
      }
    } else {
      sessionTokens.value.tokens = []
      sessionTokens.value.selectedToken = 0
      sessionTokens.value.sessionEnd = undefined

      persistentTokens.value.tokens = tokenList
    }

    try {
      selectToken(_currentToken)
    } catch {
      selectToken()
    }
  }

  function addToken(token: TokenList[number]) {
    if (authPersistence.value === 'session') {
      sessionTokens.value.tokens.push(token)
    } else {
      persistentTokens.value.tokens.push(token)
    }
  }

  function removeToken(token: { token: string }) {
    const storage = authPersistence.value === 'session' ? sessionTokens.value : persistentTokens.value
    const tokenIdx = storage.tokens.findIndex((t) => t.token === token.token)
    if (tokenIdx > -1) {
      if (storage.selectedToken >= tokenIdx && tokenIdx > 0) {
        storage.selectedToken -= 1
      }
      storage.tokens.splice(tokenIdx, 1)
    }
  }

  function updateCurrentToken(token: TokenList[number]) {
    const storage = authPersistence.value === 'session' ? sessionTokens.value : persistentTokens.value
    if (storage.selectedToken > -1 && selectedToken.value != null) {
      storage.tokens.splice(storage.selectedToken, 1, token)
    }
  }

  function selectToken(token?: string) {
    const storage = authPersistence.value === 'session' ? sessionTokens.value : persistentTokens.value
    if (token == null) {
      storage.selectedToken = 0
    } else {
      const tokenIdx = storage.tokens.findIndex((t) => t.token === token)
      if (tokenIdx === -1) throw new Error('Token not found among available tokens')
      storage.selectedToken = tokenIdx
    }
  }

  function clearAllTokens() {
    persistentTokens.value.tokens = []
    persistentTokens.value.selectedToken = 0
    sessionTokens.value.tokens = []
    sessionTokens.value.selectedToken = 0
    sessionTokens.value.sessionEnd = undefined
  }

  return {
    selectedToken,
    isCompanyWithoutSignatory,
    tokensList,
    authPersistence,
    sessionEnd,
    isSessionExpired,
    setTokenList,
    updateCurrentToken,
    selectToken,
    addToken,
    removeToken,
    clearAllTokens,
  }
})

export default useAuthStore
